import React, { useState, useEffect } from 'react';
import { 
  Button, Input, Label, Textarea, Badge
} from '../ui/UIComponents';
import { Role, RoleFormData } from '../../types/rbac.types';
import { createRole, updateRole, getApplications } from '../../services/rbacApi';
import TreeView, { TreeNode } from '../ui/TreeView';
import { Globe, Layers } from 'lucide-react';

interface RoleFormProps {
  isOpen: boolean;
  onClose: (refreshData?: boolean) => void;
  role: Role | null;
  applicationId?: string;
}

const RoleForm: React.FC<RoleFormProps> = ({ isOpen, onClose, role, applicationId }) => {
  const [formData, setFormData] = useState<RoleFormData>({
    name: '',
    description: '',
    application_id: applicationId || '',
    is_system_role: false
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [applications, setApplications] = useState<any[]>([]);
  const [loadingApps, setLoadingApps] = useState<boolean>(false);

  // Fetch applications on component mount
  useEffect(() => {
    fetchApplications();
  }, []);

  // Initialize form with role data if editing
  useEffect(() => {
    if (role) {
      setFormData({
        name: role.name,
        description: role.description || '',
        application_id: role.application_id || '',
        is_system_role: role.is_system_role
      });
    } else {
      setFormData({
        name: '',
        description: '',
        application_id: applicationId || '',
        is_system_role: false
      });
    }
  }, [role, applicationId]);

  const fetchApplications = async () => {
    try {
      setLoadingApps(true);
      const data = await getApplications();
      setApplications(data || []);
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoadingApps(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error for the field being edited
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };
  
  // Convert applications to tree nodes
  const getApplicationTreeNodes = (): TreeNode[] => {
    const allAppsNode: TreeNode = {
      id: '',
      name: 'Global Role (All Applications)',
      icon: <Globe size={18} className="text-green-400" />,
    };
    
    const appNodes: TreeNode[] = applications.map(app => ({
      id: app.id,
      name: app.name,
      icon: <Layers size={18} className="text-blue-400" />,
      data: app,
      badge: app.status === 'active' ? (
        <Badge variant="secondary" className="bg-green-900/30 text-green-300 border border-green-700/30 text-xs">
          Active
        </Badge>
      ) : app.status === 'development' ? (
        <Badge variant="secondary" className="bg-amber-900/30 text-amber-300 border border-amber-700/30 text-xs">
          Dev
        </Badge>
      ) : (
        <Badge variant="secondary" className="bg-gray-900/30 text-gray-300 border border-gray-700/30 text-xs">
          Inactive
        </Badge>
      )
    }));
    
    return [allAppsNode, ...appNodes];
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      // Submitting role form data
      
      if (role) {
        // Update existing role
        await updateRole(role.id, formData);
      } else {
        // Create new role
        await createRole(formData);
      }
      
      onClose(true); // Close form and refresh data
    } catch (err: any) {
      console.error('Error saving role:', err);
      setSubmitError(err.response?.data?.error || 'Failed to save role');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-gray-800 rounded-lg shadow-xl w-full max-w-2xl overflow-hidden">
        {/* Header */}
        <div className="border-b border-gray-800 px-6 py-4">
          <h2 className="text-xl font-semibold text-gray-100">
            {role ? 'Edit Role' : 'Create New Role'}
          </h2>
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col h-full max-h-[calc(100vh-150px)]">
          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {submitError && (
              <div className="mb-4 p-3 bg-red-900/30 border border-red-500 text-red-300 rounded">
                {submitError}
              </div>
            )}
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="name" className="block mb-1">Role Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter role name"
                  className={errors.name ? 'border-red-500' : ''}
                />
                {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
              </div>
              
              <div>
                <Label htmlFor="description" className="block mb-1">Description</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Enter role description"
                  rows={3}
                />
              </div>
              
              <div>
                <Label htmlFor="application_id" className="block mb-1">Application (Optional)</Label>
                <div className="border border-gray-800 rounded-lg overflow-hidden bg-gray-900/50">
                  <TreeView 
                    nodes={getApplicationTreeNodes()} 
                    onSelect={(node) => {
                      setFormData(prev => ({ ...prev, application_id: node.id }));
                      if (errors.application_id) {
                        setErrors(prev => ({ ...prev, application_id: '' }));
                      }
                    }}
                    selectedId={formData.application_id}
                    className="max-h-[200px] overflow-y-auto py-1.5"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-400">
                  Select "Global Role" to create a role that applies to all applications
                </p>
                {errors.application_id && <p className="mt-1 text-sm text-red-500">{errors.application_id}</p>}
              </div>
            </div>
          </div>
          
          {/* Footer with buttons */}
          <div className="border-t border-gray-800 px-6 py-4 bg-gray-900 flex justify-end gap-3">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onClose()}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isSubmitting}
              isLoading={isSubmitting}
            >
              {role ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RoleForm; 